import { useEffect, useRef, useState } from 'react';
import { magicServices } from '../../../services/InspectionOneServices';
import { generateUniqueRandomKey } from '../../../utils/randomKey';
import { getNextPageAfterDelete, convertToUTC, toLocalInputValue } from '../../../utils/common';
import ObjectDetectionFilterModal from '../../../components/UI/objectdetection-filter-modal';
import './vision-training.css';

type Label = { labelName: string; labelUniqueId: string };

type Ann = {
  uniqueId?: string;
  type: 'rect'|'circle'|'freedraw';
  coordinates: {x:number;y:number}[];
  label: string;
  labelUniqueId: string;
  source: 'user'|'system';
  isDraft?: boolean;
};

type VisionTrainingProps = {
  iteration: number;
  magicCommonData: any;
  handleEditorClose: () => void;
};

function VisionTraining({ iteration, magicCommonData, handleEditorClose }: VisionTrainingProps) {
  const ENABLE_LABEL_CREATION = false;       // set false to hide + button and block typing new label names
  const ENABLE_RECT_ANNOTATION = true;      // set true to show rectangle annotation tool
  const ENABLE_CIRCLE_ANNOTATION = false;    // set true to show circle annotation tool
  const ENABLE_FREEDRAW_ANNOTATION = false;  // set true to show free draw annotation tool

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mmRef = useRef<HTMLCanvasElement>(null);
  const mmVpRef = useRef<HTMLDivElement>(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);

  const imgEl = useRef(new Image());
  const afRef = useRef<number | null>(null);
  const tgt = useRef({ zoom: 1, panX: 0, panY: 0 });
  const st = useRef({
    tool: 'rect', zoom: 1, panX: 0, panY: 0,
    isPanning: false, isDrawing: false, spaceHeld: false,
    startX: 0, startY: 0, lastX: 0, lastY: 0,
    annotations: [] as Ann[], undoStack: [] as Ann[][], redoStack: [] as Ann[][],
    selectedAnnotation: null as number | null, hoveredAnnotation: null as number | null, imageLoaded: false,
    freeDrawPoints: [] as { x: number; y: number }[], draftAnnotation: null as Ann | null,
  });

  const fns = useRef({ zoomIn: () => {}, zoomOut: () => {}, zoomFit: () => {}, undo: () => {}, redo: () => {}, deleteSelected: () => {}, render: () => {}, loadImage: (_idx: number) => {}, saveAnnotations: () => {} });

  const imgIdxRef = useRef(0);
  const dataRef = useRef<any>({ labels: [], defaultLabels: [], sasToken: '', totalCount: 0 });
  const currentIterationRef = useRef<any>(0);
  const currentImageCountRef = useRef(1);
  const onImageReadyRef = useRef<() => void>(() => {});

  const [zoomTxt, setZoomTxt] = useState('100%');
  const [annotDone, setAnnotDone] = useState(0);
  const [annotLeft, setAnnotLeft] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [noPending, setNoPending] = useState(false);
  const [imgInfo, setImgInfo] = useState({ plateTypeName: 'Plate Type', plateTypeValue: '', date: '', time: '', cameraPosition: '', predicted: 'notok' as 'ok' | 'notok', hasCameraPosition: false, hasRange: false, startArea: null as number | null, endArea: null as number | null });
  const [labels, setLabels] = useState<Label[]>([]);
  const [labelCounts, setLabelCounts] = useState<Record<string, number>>({});
  const [hasUserAnnotations, setHasUserAnnotations] = useState(false);
  const [toolUI, setToolUI] = useState('rect');
  const [actual, setActual] = useState<'ok' | 'notok'>('notok');
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [isFiltreModalOpen, setIsFiltreModalOpen] = useState(false);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const [statusUpdated, setStatusUpdated] = useState('1');
  const [startValue, setStartValue] = useState<any>('');
  const [endValue, setEndValue] = useState<any>('');
  const [predictionValue, setPredictionValue] = useState<string>('-1');
  const [actualValue, setActualValue] = useState<string>('-1');
  const [statusValue, setStatusValue] = useState('1');
  const [SelectedModelDefect, setSelectedModelDefect] = useState('');
  const [modelDefectLabels, setModelDefectLabels] = useState<any[]>([]);
  const [rawDefaultLabels, setRawDefaultLabels] = useState<any[]>([]);
  const [ignoreIds, setIgnoreIds] = useState<string[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [labelSearch, setLabelSearch] = useState('');
  const [selLabel, setSelLabel] = useState<Label | null>(null);

  const labelsRef = useRef<Label[]>([]);
  const openPopupRef = useRef<() => void>(() => {});
  const openEditPopupRef = useRef<(idx: number) => void>((_idx: number) => {});
  const editingAnnIdxRef = useRef<number>(-1);
  const popupModeRef = useRef<'annotation' | 'addLabel' | 'editAnnotation'>('annotation');
  const isReadOnlyRef = useRef(false);
  const statusUpdatedRef = useRef('1');
  const enableAnnotationRef = useRef(ENABLE_RECT_ANNOTATION || ENABLE_CIRCLE_ANNOTATION || ENABLE_FREEDRAW_ANNOTATION);

  const normalizeModelValidationDetails = (details: any) => {
    if (!details) return {};
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch {
        return {};
      }
    }
    return details;
  };

  const setCurrentImageActual = (value: '1' | '2') => {
    const imgDetail = dataRef.current.labels?.[imgIdxRef.current];
    if (!imgDetail) return;
    imgDetail.modelValidationDetails = {
      ...normalizeModelValidationDetails(imgDetail.modelValidationDetails),
      actual: value,
    };
    setActual(value === '1' ? 'ok' : 'notok');
  };

  const updateActualFromAnnotations = (annotations: Ann[]) => {
    setCurrentImageActual(annotations.some((a: Ann) => a.source === 'user') ? '2' : '1');
  };

  useEffect(() => { labelsRef.current = labels; }, [labels]);
  useEffect(() => {
    statusUpdatedRef.current = statusUpdated;
    isReadOnlyRef.current = statusUpdated === '2';
    if (statusUpdated === '2') {
      st.current.tool = 'pan';
      setToolUI('pan');
    } else {
      st.current.tool = 'rect';
      setToolUI('rect');
    }
  }, [statusUpdated]);

  useEffect(() => { setAccordionOpen(noPending); }, [noPending]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    openPopupRef.current = () => { popupModeRef.current = 'annotation'; setLabelSearch(''); setSelLabel(null); setPopupOpen(true); };
    openEditPopupRef.current = (idx: number) => {
      editingAnnIdxRef.current = idx;
      popupModeRef.current = 'editAnnotation';
      const ann = st.current.annotations[idx];
      const currentLabel = labelsRef.current.find(l => l.labelUniqueId === ann?.labelUniqueId) ?? null;
      setSelLabel(currentLabel);
      setLabelSearch('');
      setPopupOpen(true);
    };
  });

  // ── API helpers ──────────────────────────────────────────────────────────────

  // pageCount: 1 — same as MultiImageEditor (one image per fetch)
  const predictionFiltreOption = [
    { id: '-1', label: 'All' },
    { id: '2', label: 'NOT OK' },
    { id: '1', label: 'OK' },
  ];
  const annotationStatus = [
    { id: '1', label: 'Pending' },
    { id: '2', label: 'Completed' },
  ];

  useEffect(() => {
    const list = [
      { id: '', label: 'All' },
      ...rawDefaultLabels
        .filter((item: any) => item.labelIdentifier?.trim() && !ignoreIds.includes(item.uniqueId))
        .map((item: any) => ({ id: item.uniqueId, label: item.labelName })),
    ];
    setModelDefectLabels(list);
  }, [rawDefaultLabels, ignoreIds]);

  const fetchImage = async (imageNumber: number, additionalPayload: any = null) => {
    setIsLoading(true);
    try {
      let requestData: any = {
        uniqueId: magicCommonData.uniqueId,
        iteration,
        pageCount: 1,
        pageNumber: imageNumber,
        prediction: predictionValue,
        actual: actualValue,
        status: statusValue,
        filtreLabelUniqueId: SelectedModelDefect,
      };

      if (additionalPayload) {
        requestData = { ...requestData, ...additionalPayload };
      } else {
        if (startValue !== '' && endValue !== '') {
          requestData.fromDate = convertToUTC(startValue);
          requestData.toDate = convertToUTC(endValue);
        }
        if (predictionValue !== '-1') {
          requestData.prediction = predictionValue;
        }
      }

      setStatusUpdated(requestData.status ?? statusValue);

      const response = await magicServices.MagicLabelDetailfetch(requestData);
      if (response.success) {
        const d = response.data;
        const normalizedLabels = (d.labels || []).map((label: any) => ({
          ...label,
          modelValidationDetails: normalizeModelValidationDetails(label.modelValidationDetails),
          annotationDetails: (label.annotationDetails || []).map((annotation: any) => ({
            ...annotation,
            modelValidationDetails: normalizeModelValidationDetails(annotation.modelValidationDetails),
          })),
        }));
        dataRef.current = { ...d, labels: normalizedLabels, parsedIgnoreIds: [], magicDetails: d.magicDetails || null };

        currentImageCountRef.current = imageNumber;
        setCurrentPage(imageNumber);
        setAnnotDone(d.totalImagesCompletedCount ?? 0);
        setAnnotLeft(d.totalCount ?? 0);

        // Parse ignoreDefaultLabels
        const parsedIgnoreIds: string[] = [];
        if (d.magicAdditionalConfigurations) {
          try {
            const cfg = JSON.parse(d.magicAdditionalConfigurations);
            parsedIgnoreIds.push(...(cfg.ignoreDefaultLabels || '').split(',').map((s: string) => s.trim()).filter(Boolean));
          } catch {}
        }
        dataRef.current.parsedIgnoreIds = parsedIgnoreIds;
        setRawDefaultLabels(d.defaultLabels || []);
        setIgnoreIds(parsedIgnoreIds);

        const newLabels: Label[] = (d.defaultLabels || [])
          .filter((l: any) => !parsedIgnoreIds.includes(l.uniqueId))
          .map((l: any) => ({ labelName: l.labelName, labelUniqueId: l.uniqueId }));
        setLabels(newLabels);
        labelsRef.current = newLabels;

        // Derive currentIteration (same logic as MultiImageEditor)
        const sorted = [...(d.iterationsList || [])].sort((a: any, b: any) => Number(a.iteration) - Number(b.iteration));
        const training = sorted.filter((i: any) => Number(i.isTraining) === 1);
        if (training.length === 0) {
          const completed = sorted.filter((i: any) => Number(i.isCompleted) === 1);
          currentIterationRef.current = completed.length > 0 ? completed[completed.length - 1].iteration : 0;
        } else {
          currentIterationRef.current = training[training.length - 1].iteration;
        }

        if ((d.labels || []).length > 0) {
          setNoPending(false);
          onImageReadyRef.current = () => setIsLoading(false);
          fns.current.loadImage(0);
        } else {
          setNoPending(true);
          st.current.imageLoaded = false;
          st.current.annotations = [];
          st.current.selectedAnnotation = null;
          st.current.hoveredAnnotation = null;
          st.current.draftAnnotation = null;
          st.current.undoStack = [];
          st.current.redoStack = [];
          setLabelCounts({});
          setHasUserAnnotations(false);
          setActual('notok');
          setImgInfo({ plateTypeName: 'Plate Type', plateTypeValue: '', date: '', time: '', cameraPosition: '', predicted: 'notok', hasCameraPosition: false, hasRange: false, startArea: null, endArea: null });
          fns.current.render();
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('[VisionTraining] Fetch error:', error);
      setIsLoading(false);
    }
  };

  const handleInputChange = (key: any, option: any) => {
    if (key === 'predict') setPredictionValue(option.id);
    else if (key === 'actual') setActualValue(option.id);
    else if (key === 'status') {
      setStatusValue(option.id);
      if (option.id === '1') setActualValue('-1');
    }
    else if (key === 'modelDefect') setSelectedModelDefect(option.id);
  };

  const handleFormSubmit = () => {
    currentImageCountRef.current = 1;
    fetchImage(1);
    setIsFiltreModalOpen(false);
  };

  const saveCurrentImage = async () => {
    const images = dataRef.current.labels;
    if (!images.length) return;
    setIsLoading(true);

    // Completed filter — skip save, navigate forward normally
    if (statusUpdated === '2') {
      const total = dataRef.current.totalCount ?? 0;
      const nextImageNumber = currentImageCountRef.current < total
        ? currentImageCountRef.current + 1
        : currentImageCountRef.current;
      currentImageCountRef.current = nextImageNumber;
      await fetchImage(nextImageNumber);
      return;
    }

    fns.current.saveAnnotations();
    const imageDetail = images[imgIdxRef.current];
    const modelValidationDetails = normalizeModelValidationDetails(imageDetail.modelValidationDetails);
    const currentActualValue = String(modelValidationDetails.actual ?? '');
    imageDetail.modelValidationDetails = {
      ...modelValidationDetails,
      actual: currentActualValue === '1' || currentActualValue === '2'
        ? currentActualValue
        : actual === 'ok' ? '1' : '2',
    };
    try {
      const response = await magicServices.MagicIterationSave({
        ...magicCommonData,
        ...(dataRef.current.magicDetails || {}),
        currentIteration: currentIterationRef.current,
        defaultLabels: dataRef.current.defaultLabels,
        labels: [
          {
            ...imageDetail,
            modelValidationDetails: JSON.stringify(imageDetail.modelValidationDetails),
            annotationDetails: imageDetail.annotationDetails,
          },
        ],
      });
      if (response.success) {
        const next = getNextPageAfterDelete(currentImageCountRef.current, dataRef.current.totalCount ?? 0);
        const nextImageNumber = next.current > 0 ? next.current : 1;
        currentImageCountRef.current = nextImageNumber;
        await fetchImage(nextImageNumber);
      }
    } catch (error) {
      console.error('[VisionTraining] Save error:', error);
    }
  };

  const handleNext = async () => { await saveCurrentImage(); };

  const handleDeleteImage = async () => {
    setShowDeleteImageModal(false);
    const images = dataRef.current.labels;
    if (!images.length) return;
    const imageDetail = images[imgIdxRef.current];
    try {
      const response = await magicServices.MagicIterationSave({
        ...magicCommonData,
        ...(dataRef.current.magicDetails || {}),
        currentIteration: currentIterationRef.current,
        defaultLabels: dataRef.current.defaultLabels,
        isDeleted: 1,
        labels: [{
          ...imageDetail,
          isDeleted: 1,
          isImageInvalid: 1,
          modelValidationDetails: JSON.stringify(imageDetail.modelValidationDetails),
          annotationDetails: (imageDetail.annotationDetails || []).map((a: any) => ({ ...a, isDeleted: 1 })),
        }],
      });
      if (response.success) {
        const next = getNextPageAfterDelete(currentImageCountRef.current, dataRef.current.totalCount ?? 0);
        const nextImageNumber = next.current > 0 ? next.current : 1;
        currentImageCountRef.current = nextImageNumber;
        await fetchImage(nextImageNumber);
      }
    } catch (error) {
      console.error('[VisionTraining] Delete image error:', error);
    }
  };

  // Initial load — mirrors image editor: default date range today 06:00 → +24 h
  useEffect(() => {
    const now = new Date();
    let startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 6, 0, 0);
    if (now < startLocal) {
      startLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 6, 0, 0);
    }
    const endLocal = new Date(startLocal.getTime() + 24 * 60 * 60 * 1000);

    const predictionData = predictionFiltreOption.length > 0 ? predictionFiltreOption[0].id : predictionValue;
    const actualData    = predictionFiltreOption.length > 0 ? predictionFiltreOption[0].id : actualValue;
    const statusData    = annotationStatus.length > 0       ? annotationStatus[0].id       : statusValue;

    const payload = {
      fromDate:   startLocal.toISOString(),
      toDate:     endLocal.toISOString(),
      prediction: predictionData,
      actual:     actualData,
      status:     statusData,
    };

    setStartValue(toLocalInputValue(startLocal));
    setEndValue(toLocalInputValue(endLocal));
    setPredictionValue(predictionData);
    setActualValue(actualData);
    setStatusValue(statusData);

    fetchImage(1, payload);
  }, []);

  // ── Canvas / drawing ─────────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current!;
    const container = containerRef.current!;
    const img = imgEl.current;
    const s = st.current;
    const t = tgt.current;

    const toImg = (sx: number, sy: number) => ({ x: (sx - s.panX) / s.zoom, y: (sy - s.panY) / s.zoom });
    const clampPt = (p: {x:number,y:number}) => ({ x: Math.max(0, Math.min(img.width, p.x)), y: Math.max(0, Math.min(img.height, p.y)) });
    const toImgC = (sx: number, sy: number) => clampPt(toImg(sx, sy));
    const inImage = (sx: number, sy: number) => { const p = toImg(sx, sy); return s.imageLoaded && p.x >= 0 && p.x <= img.width && p.y >= 0 && p.y <= img.height; };

    const bounds = (a: Ann) => {
      if (a.type === 'rect') { const c = a.coordinates; return { x: Math.min(c[0].x, c[1].x), y: Math.min(c[0].y, c[1].y), w: Math.abs(c[1].x - c[0].x), h: Math.abs(c[1].y - c[0].y) }; }
      if (a.type === 'circle') { const cx = a.coordinates[0].x, cy = a.coordinates[0].y, r = Math.sqrt((a.coordinates[1].x - cx) ** 2 + (a.coordinates[1].y - cy) ** 2); return { x: cx - r, y: cy - r, w: r * 2, h: r * 2 }; }
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      a.coordinates.forEach(p => { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); });
      return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
    };

    const hit = (a: Ann, px: number, py: number) => {
      if (a.type === 'rect') { const b = bounds(a); return px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h; }
      if (a.type === 'circle') { const cx = a.coordinates[0].x, cy = a.coordinates[0].y, r = Math.sqrt((a.coordinates[1].x - cx) ** 2 + (a.coordinates[1].y - cy) ** 2); return Math.sqrt((px - cx) ** 2 + (py - cy) ** 2) <= r; }
      const b = bounds(a); return px >= b.x - 5 && px <= b.x + b.w + 5 && py >= b.y - 5 && py <= b.y + b.h + 5;
    };

    const syncCounts = () => {
      const c: Record<string, number> = {};
      labelsRef.current.forEach(l => { c[l.labelUniqueId] = s.annotations.filter(a => a.labelUniqueId === l.labelUniqueId).length; });
      setLabelCounts({ ...c });
      setHasUserAnnotations(s.annotations.some((a: Ann) => a.source === 'user'));
    };

    const render = () => {
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#d1d5db'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.save(); ctx.translate(s.panX, s.panY); ctx.scale(s.zoom, s.zoom);
      if (s.imageLoaded) ctx.drawImage(img, 0, 0);

      const all = [...s.annotations];
      if (s.draftAnnotation) all.push({ ...s.draftAnnotation, isDraft: true });

      all.forEach((a, i) => {
        const col = a.source === 'system' ? '#ef4444' : '#0078d4';
        const isSel = s.selectedAnnotation === i && !a.isDraft;
        ctx.strokeStyle = col; ctx.lineWidth = (isSel ? 3 : 2) / s.zoom;
        const b = bounds(a);
        if (a.type === 'rect') { ctx.beginPath(); ctx.rect(b.x, b.y, b.w, b.h); ctx.stroke(); }
        else if (a.type === 'circle') {
          const cx = a.coordinates[0].x, cy = a.coordinates[0].y, r = Math.sqrt((a.coordinates[1].x - cx) ** 2 + (a.coordinates[1].y - cy) ** 2);
          ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
        } else if (a.type === 'freedraw' && a.coordinates.length > 1) {
          ctx.fillStyle = 'transparent'; ctx.beginPath(); ctx.moveTo(a.coordinates[0].x, a.coordinates[0].y);
          for (let j = 1; j < a.coordinates.length; j++) ctx.lineTo(a.coordinates[j].x, a.coordinates[j].y);
          ctx.stroke();
        }
        if (a.label) {
          const th = 22 / s.zoom, fs = 11 / s.zoom;
          ctx.fillStyle = col; ctx.font = `700 ${fs}px Nunito`;
          const tw = ctx.measureText(a.label).width + 12 / s.zoom;
          ctx.beginPath(); (ctx as any).roundRect(b.x, b.y - th - 4 / s.zoom, tw, th, 6 / s.zoom); ctx.fill();
          ctx.fillStyle = '#fff'; ctx.fillText(a.label, b.x + 6 / s.zoom, b.y - 10 / s.zoom);
        }
        if (!a.isDraft && s.hoveredAnnotation === i) {
          const iconR = 8 / s.zoom;
          const iconY = b.y + iconR;
          const delX = b.x + b.w - iconR;
          const editX = delX - iconR * 2.5;
          ctx.font = `bold ${13 / s.zoom}px Arial`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          // Edit icon — top-right, left of delete
          ctx.fillStyle = '#0078d4';
          ctx.beginPath(); ctx.arc(editX, iconY, iconR, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#fff'; ctx.fillText('✎', editX, iconY);
          // Delete icon — top-right corner inside
          ctx.fillStyle = '#ef4444';
          ctx.beginPath(); ctx.arc(delX, iconY, iconR, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#fff'; ctx.fillText('×', delX, iconY);
          ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
        }
        if (isSel) {
          ctx.fillStyle = '#fff'; ctx.strokeStyle = col; ctx.lineWidth = 2 / s.zoom;
          const hs = 10 / s.zoom;
          const handles = a.type === 'circle'
            ? (() => { const cx = a.coordinates[0].x, cy = a.coordinates[0].y, r = Math.sqrt((a.coordinates[1].x - cx) ** 2 + (a.coordinates[1].y - cy) ** 2); return [[cx, cy - r], [cx + r, cy], [cx, cy + r], [cx - r, cy]]; })()
            : [[b.x, b.y], [b.x + b.w, b.y], [b.x, b.y + b.h], [b.x + b.w, b.y + b.h]];
          handles.forEach(([hx, hy]) => { ctx.beginPath(); ctx.arc(hx, hy, hs / 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); });
        }
      });

      if (s.isDrawing) {
        ctx.strokeStyle = '#0078d4'; ctx.lineWidth = 2 / s.zoom;
        ctx.setLineDash([6 / s.zoom, 4 / s.zoom]);
        if (s.tool === 'rect') {
          const p1 = toImgC(s.startX, s.startY), p2 = toImgC(s.lastX, s.lastY);
          ctx.beginPath(); ctx.rect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y); ctx.stroke();
        } else if (s.tool === 'circle') {
          const c = toImgC(s.startX, s.startY), e2 = toImgC(s.lastX, s.lastY);
          ctx.beginPath(); ctx.arc(c.x, c.y, Math.sqrt((e2.x - c.x) ** 2 + (e2.y - c.y) ** 2), 0, Math.PI * 2); ctx.stroke();
        } else if (s.tool === 'freedraw' && s.freeDrawPoints.length > 0) {
          ctx.setLineDash([]); ctx.lineWidth = 2.5 / s.zoom;
          ctx.beginPath(); ctx.moveTo(s.freeDrawPoints[0].x, s.freeDrawPoints[0].y);
          for (let j = 1; j < s.freeDrawPoints.length; j++) ctx.lineTo(s.freeDrawPoints[j].x, s.freeDrawPoints[j].y);
          ctx.stroke();
        }
        ctx.setLineDash([]);
      }
      ctx.restore();

      const mc = mmRef.current, vp = mmVpRef.current;
      if (mc && vp && s.imageLoaded && img.width > 0) {
        const mctx = mc.getContext('2d')!, mm = mc.parentElement!;
        mc.width = mm.clientWidth; mc.height = mm.clientHeight;
        const sc = Math.min(mc.width / img.width, mc.height / img.height);
        const dx = (mc.width - img.width * sc) / 2, dy = (mc.height - img.height * sc) / 2;
        mctx.clearRect(0, 0, mc.width, mc.height);
        mctx.drawImage(img, dx, dy, img.width * sc, img.height * sc);
        vp.style.left = ((-s.panX / s.zoom) * sc + dx) + 'px';
        vp.style.top = ((-s.panY / s.zoom) * sc + dy) + 'px';
        vp.style.width = ((canvas.width / s.zoom) * sc) + 'px';
        vp.style.height = ((canvas.height / s.zoom) * sc) + 'px';
      }
    };

    const resizeCanvas = () => { canvas.width = container.clientWidth; canvas.height = container.clientHeight; render(); };

    const zoomFit = () => {
      if (!s.imageLoaded) return;
      const p = 40;
      t.zoom = s.zoom = Math.min((canvas.width - p * 2) / img.width, (canvas.height - p * 2) / img.height);
      t.panX = s.panX = (canvas.width - img.width * s.zoom) / 2;
      t.panY = s.panY = (canvas.height - img.height * s.zoom) / 2;
      render(); setZoomTxt(Math.round(s.zoom * 100) + '%');
    };

    const animZoom = () => {
      const e = 0.15;
      s.zoom += (t.zoom - s.zoom) * e; s.panX += (t.panX - s.panX) * e; s.panY += (t.panY - s.panY) * e;
      if (Math.abs(t.zoom - s.zoom) > 0.001 || Math.abs(t.panX - s.panX) > 0.5 || Math.abs(t.panY - s.panY) > 0.5) {
        render(); afRef.current = requestAnimationFrame(animZoom);
      } else { s.zoom = t.zoom; s.panX = t.panX; s.panY = t.panY; render(); afRef.current = null; }
      setZoomTxt(Math.round(s.zoom * 100) + '%');
    };

    const smoothZoom = (nz: number, cx?: number, cy?: number) => {
      cx = cx ?? canvas.width / 2; cy = cy ?? canvas.height / 2;
      nz = Math.max(0.1, Math.min(10, nz));
      const r = nz / s.zoom; t.panX = cx - (cx - s.panX) * r; t.panY = cy - (cy - s.panY) * r; t.zoom = nz;
      if (!afRef.current) afRef.current = requestAnimationFrame(animZoom);
    };

    fns.current.zoomIn = () => smoothZoom(t.zoom * 1.25);
    fns.current.zoomOut = () => smoothZoom(t.zoom * 0.8);
    fns.current.zoomFit = zoomFit;
    fns.current.undo = () => { if (!s.undoStack.length) return; s.redoStack.push(JSON.parse(JSON.stringify(s.annotations))); s.annotations = s.undoStack.pop()!; s.selectedAnnotation = null; syncCounts(); updateActualFromAnnotations(s.annotations); render(); };
    fns.current.redo = () => { if (!s.redoStack.length) return; s.undoStack.push(JSON.parse(JSON.stringify(s.annotations))); s.annotations = s.redoStack.pop()!; s.selectedAnnotation = null; syncCounts(); updateActualFromAnnotations(s.annotations); render(); };
    fns.current.deleteSelected = () => { if (s.selectedAnnotation !== null) { s.undoStack.push(JSON.parse(JSON.stringify(s.annotations))); s.annotations.splice(s.selectedAnnotation, 1); s.selectedAnnotation = null; syncCounts(); updateActualFromAnnotations(s.annotations); render(); } };
    fns.current.render = render;

    const ANN_TYPE: Record<string, Ann['type']> = { '1': 'rect', '2': 'circle', '3': 'freedraw' };
    const REV_TYPE: Record<Ann['type'], string> = { rect: '1', circle: '2', freedraw: '3' };

    const saveAnnotations = () => {
      const idx = imgIdxRef.current;
      const images = dataRef.current.labels;
      if (idx < 0 || idx >= images.length) return;
      const existingAnnotations = images[idx].annotationDetails || [];
      images[idx].annotationDetails = s.annotations.map((a: Ann, i: number) => {
        const orig = (a.uniqueId
          ? existingAnnotations.find((item: any) => item.uniqueId === a.uniqueId)
          : null) ?? existingAnnotations[i] ?? {};
        const origModelValidationDetails = normalizeModelValidationDetails(orig.modelValidationDetails);
        return {
          uniqueId: a.uniqueId ?? orig.uniqueId ?? generateUniqueRandomKey(),
          annotationId: orig.annotationId ?? 0,
          labelName: a.label,
          labelUniqueId: a.labelUniqueId || orig.labelUniqueId || '',
          type: REV_TYPE[a.type] ?? '1',
          stroke: orig.stroke ?? '#FCE049',
          strokeWidth: 2,
          fill: 'transparent',
          color: '',
          coordinates: a.coordinates,
          modelValidationDetails: {
            ...origModelValidationDetails,
            isAuto: a.source === 'system' ? 1 : 0,
            label: a.labelUniqueId || '',
          },
          size: null,
          isDeleted: 0,
        };
      });
    };

    const loadImageByIdx = (idx: number) => {
      const images = dataRef.current.labels;
      if (idx < 0 || idx >= images.length) return;
      const imgData = images[idx];
      imgIdxRef.current = idx;

      const ignoreIds = dataRef.current.parsedIgnoreIds || [];
      s.annotations = (imgData.annotationDetails || [])
        .filter((a: any) => !a.isDeleted && !ignoreIds.includes(a.labelUniqueId))
        .map((a: any) => {
          const annModelValidationDetails = normalizeModelValidationDetails(a.modelValidationDetails);
          return {
            type: ANN_TYPE[a.type] ?? 'rect',
            uniqueId: a.uniqueId,
            coordinates: a.coordinates,
            label: a.labelName,
            labelUniqueId: a.labelUniqueId ?? '',
            source: annModelValidationDetails?.isAuto === 1 ? 'system' : 'user',
          };
        });
      s.selectedAnnotation = null;
      s.hoveredAnnotation = null;
      s.draftAnnotation = null;
      s.undoStack = [];
      s.redoStack = [];
      s.imageLoaded = false;
      render();

      let sasRetried = false;
      img.onerror = () => {
        if (!sasRetried && dataRef.current.sasToken) {
          sasRetried = true;
          console.warn('[VisionTraining] SAS load failed, retrying without token:', img.src);
          img.src = imgData.magicUrl;
        } else {
          console.error('[VisionTraining] Image failed to load:', img.src);
        }
      };
      img.onload = () => { s.imageLoaded = true; resizeCanvas(); zoomFit(); syncCounts(); onImageReadyRef.current(); onImageReadyRef.current = () => {}; };
      const sas = dataRef.current.sasToken ?? '';
      img.src = sas ? imgData.magicUrl + sas : imgData.magicUrl;

      const mv = normalizeModelValidationDetails(imgData.modelValidationDetails);
      const dt = mv.dateTime ? new Date(mv.dateTime) : null;
      setImgInfo({
        plateTypeName: mv.typeName || 'Plate Type',
        plateTypeValue: mv.typeValue || '—',
        date: dt ? dt.toLocaleDateString() : '—',
        time: dt ? dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
        cameraPosition: mv.cameraPosition || '',
        predicted: String(mv.predicted) === '1' ? 'ok' : 'notok',
        hasCameraPosition: mv.cameraPosition != null && mv.cameraPosition !== '',
        ...(() => {
          const annWithRange = (imgData.annotationDetails || []).find((a: any) => {
            const annModelValidationDetails = normalizeModelValidationDetails(a.modelValidationDetails);
            return annModelValidationDetails?.startArea != null && annModelValidationDetails?.endArea != null;
          });
          const annModelValidationDetails = annWithRange ? normalizeModelValidationDetails(annWithRange.modelValidationDetails) : null;
          const startArea = annModelValidationDetails?.startArea;
          const endArea = annModelValidationDetails?.endArea;
          const hasRange = startArea != null && endArea != null && String(startArea).trim() !== '' && String(endArea).trim() !== '';
          return { hasRange, startArea: hasRange ? Number(startArea) : null, endArea: hasRange ? Number(endArea) : null };
        })(),
      });
      const hasExistingUserAnns = s.annotations.some((a: Ann) => a.source === 'user');
      setHasUserAnnotations(hasExistingUserAnns);
      const actualValue = String(statusUpdatedRef.current === '1' ? mv.predicted : mv.actual);
      if (actualValue === '1' || actualValue === '2') {
        setCurrentImageActual(actualValue);
      } else {
        setCurrentImageActual(hasExistingUserAnns ? '2' : (String(mv.predicted) === '1' ? '1' : '2'));
      }
    };

    fns.current.loadImage = loadImageByIdx;
    fns.current.saveAnnotations = saveAnnotations;

    const onWheel = (e: WheelEvent) => { e.preventDefault(); const r = canvas.getBoundingClientRect(); smoothZoom(t.zoom * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX - r.left, e.clientY - r.top); };
    canvas.addEventListener('wheel', onWheel, { passive: false });

    const onMouseDown = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect(), mx = e.clientX - r.left, my = e.clientY - r.top;
      if (e.button === 2) return;
      if (e.button === 0 && !s.spaceHeld && s.tool !== 'pan' && !isReadOnlyRef.current) {
        const ip = toImg(mx, my);
        const iconR = 10 / s.zoom;
        for (let i = s.annotations.length - 1; i >= 0; i--) {
          const b = bounds(s.annotations[i]);
          const drawR = 8 / s.zoom;
          const iconY = b.y + drawR;
          const delX = b.x + b.w - drawR;
          const editX = delX - drawR * 2.5;
          // Edit icon hit — top-right, left of delete
          if (Math.sqrt((ip.x - editX) ** 2 + (ip.y - iconY) ** 2) <= iconR) {
            openEditPopupRef.current(i);
            return;
          }
          // Delete icon hit — top-right corner inside
          if (Math.sqrt((ip.x - delX) ** 2 + (ip.y - iconY) ** 2) <= iconR) {
            s.undoStack.push(JSON.parse(JSON.stringify(s.annotations)));
            s.redoStack = [];
            s.annotations.splice(i, 1);
            s.selectedAnnotation = null;
            syncCounts();
            updateActualFromAnnotations(s.annotations);
            render();
            return;
          }
        }
      }
      if (s.tool === 'pan' || s.spaceHeld || e.button === 1) { s.isPanning = true; s.lastX = e.clientX; s.lastY = e.clientY; canvas.style.cursor = 'grabbing'; return; }
      if (s.tool === 'select') { const ip = toImg(mx, my); let f = -1; for (let i = s.annotations.length - 1; i >= 0; i--) { if (hit(s.annotations[i], ip.x, ip.y)) { f = i; break; } } s.selectedAnnotation = f >= 0 ? f : null; syncCounts(); render(); return; }
      if (s.tool === 'rect' || s.tool === 'circle') { if (isReadOnlyRef.current || !enableAnnotationRef.current || !inImage(mx, my)) return; s.isDrawing = true; s.startX = mx; s.startY = my; s.lastX = mx; s.lastY = my; }
      if (s.tool === 'freedraw') { if (isReadOnlyRef.current || !inImage(mx, my)) return; s.isDrawing = true; s.freeDrawPoints = [toImgC(mx, my)]; }
    };

    const onMouseMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect(), mx = e.clientX - r.left, my = e.clientY - r.top;
      if (s.isPanning) { s.panX += e.clientX - s.lastX; s.panY += e.clientY - s.lastY; t.panX = s.panX; t.panY = s.panY; s.lastX = e.clientX; s.lastY = e.clientY; render(); return; }
      if (s.isDrawing) { s.lastX = mx; s.lastY = my; if (s.tool === 'freedraw') s.freeDrawPoints.push(toImgC(mx, my)); render(); return; }
      const ip = toImg(mx, my);
      let found = -1;
      let onIcon = false;
      for (let i = s.annotations.length - 1; i >= 0; i--) {
        const b = bounds(s.annotations[i]);
        const drawR = 8 / s.zoom, iconR = 10 / s.zoom;
        const iconY = b.y + drawR, delX = b.x + b.w - drawR, editX = delX - drawR * 2.5;
        if (Math.sqrt((ip.x - editX) ** 2 + (ip.y - iconY) ** 2) <= iconR || Math.sqrt((ip.x - delX) ** 2 + (ip.y - iconY) ** 2) <= iconR) { onIcon = true; found = i; break; }
        if (hit(s.annotations[i], ip.x, ip.y)) { found = i; break; }
      }
      canvas.style.cursor = onIcon ? 'pointer' : (s.tool === 'pan' || s.spaceHeld) ? 'grab' : (s.tool === 'select' ? 'default' : 'crosshair');
      const next = found >= 0 ? found : null;
      if (next !== s.hoveredAnnotation) { s.hoveredAnnotation = next; render(); }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (s.isPanning) { s.isPanning = false; canvas.style.cursor = s.spaceHeld ? 'grab' : (s.tool === 'pan' ? 'grab' : 'crosshair'); return; }
      if (s.isDrawing) {
        s.isDrawing = false;
        const r = canvas.getBoundingClientRect(), mx = e.clientX - r.left, my = e.clientY - r.top;
        if (s.tool === 'rect') {
          const p1 = toImgC(s.startX, s.startY), p2 = toImgC(mx, my);
          if (Math.abs(p2.x - p1.x) > 3 && Math.abs(p2.y - p1.y) > 3) { s.draftAnnotation = { type: 'rect', coordinates: [{ x: Math.round(Math.min(p1.x, p2.x)), y: Math.round(Math.min(p1.y, p2.y)) }, { x: Math.round(Math.max(p1.x, p2.x)), y: Math.round(Math.max(p1.y, p2.y)) }], label: '', labelUniqueId: '', source: 'user' }; openPopupRef.current(); }
        } else if (s.tool === 'circle') {
          const c = toImgC(s.startX, s.startY), e2 = toImgC(mx, my), rad = Math.sqrt((e2.x - c.x) ** 2 + (e2.y - c.y) ** 2);
          if (rad > 3) { s.draftAnnotation = { type: 'circle', coordinates: [{ x: Math.round(c.x), y: Math.round(c.y) }, { x: Math.round(e2.x), y: Math.round(e2.y) }], label: '', labelUniqueId: '', source: 'user' }; openPopupRef.current(); }
        } else if (s.tool === 'freedraw' && s.freeDrawPoints.length > 2) {
          s.draftAnnotation = { type: 'freedraw', coordinates: s.freeDrawPoints.map(p => ({ x: Math.round(p.x), y: Math.round(p.y) })), label: '', labelUniqueId: '', source: 'user' }; openPopupRef.current();
        }
        s.freeDrawPoints = []; render();
      }
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', () => { s.isPanning = false; s.isDrawing = false; s.hoveredAnnotation = null; render(); });

    canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect(), ip = toImg(e.clientX - r.left, e.clientY - r.top);
      let f = -1; for (let i = s.annotations.length - 1; i >= 0; i--) { if (hit(s.annotations[i], ip.x, ip.y)) { f = i; break; } }
      if (f >= 0) { s.selectedAnnotation = f; syncCounts(); render(); const cm = ctxMenuRef.current!; cm.style.display = 'block'; cm.style.left = e.pageX + 'px'; cm.style.top = e.pageY + 'px'; }
    });

    const onDocClick = () => { if (ctxMenuRef.current) ctxMenuRef.current.style.display = 'none'; };
    document.addEventListener('click', onDocClick);

    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.code === 'Space' && !s.spaceHeld) { s.spaceHeld = true; canvas.style.cursor = 'grab'; e.preventDefault(); }
      const tm: Record<string, string> = { r: 'rect', R: 'rect', h: 'pan', H: 'pan', v: 'select', V: 'select', c: 'circle', C: 'circle', f: 'freedraw', F: 'freedraw' };
      if (tm[e.key]) { s.tool = tm[e.key]; setToolUI(tm[e.key]); }
      if (e.key === '0') zoomFit();
      if ((e.key === '=' || e.key === '+') && !e.ctrlKey) { e.preventDefault(); smoothZoom(t.zoom * 1.25); }
      if (e.key === '-' && !e.ctrlKey) { e.preventDefault(); smoothZoom(t.zoom * 0.8); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isReadOnlyRef.current) fns.current.deleteSelected();
      if (e.ctrlKey && e.key === 'z' && !isReadOnlyRef.current) { e.preventDefault(); fns.current.undo(); }
      if (e.ctrlKey && e.key === 'y' && !isReadOnlyRef.current) { e.preventDefault(); fns.current.redo(); }
    };
    const onKeyUp = (e: KeyboardEvent) => { const tag = (e.target as HTMLElement)?.tagName; if (tag === 'INPUT' || tag === 'TEXTAREA') return; if (e.code === 'Space') { s.spaceHeld = false; canvas.style.cursor = s.tool === 'pan' ? 'grab' : 'crosshair'; } };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', resizeCanvas);
    setTimeout(resizeCanvas, 100);

    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('resize', resizeCanvas);
      if (afRef.current) cancelAnimationFrame(afRef.current);
    };
  }, []);

  // ── Label popup ──────────────────────────────────────────────────────────────

  const closePopup = (cancel = false) => {
    if (cancel && popupModeRef.current === 'annotation') st.current.draftAnnotation = null;
    if (cancel && popupModeRef.current === 'editAnnotation') editingAnnIdxRef.current = -1;
    setPopupOpen(false);
    fns.current.render();
  };

  const confirmLabel = () => {
    const name = selLabel?.labelName || labelSearch.trim();
    if (!name) return;
    const prefix = popupModeRef.current === 'addLabel' ? 'label-' : 'anno-';
    const v: Label = selLabel ?? { labelName: name, labelUniqueId: `${prefix}${generateUniqueRandomKey()}` };
    const isNew = !labelsRef.current.find(l => l.labelUniqueId === v.labelUniqueId);
    if (isNew) {
      const nl = [...labelsRef.current, v]; setLabels(nl); labelsRef.current = nl;
    }
    if (popupModeRef.current === 'addLabel') {
      if (isNew) {
        dataRef.current.defaultLabels.push({ uniqueId: v.labelUniqueId, labelName: v.labelName, labelIdentifier: v.labelName });
      }
      closePopup(); return;
    }
    const s = st.current;
    if (popupModeRef.current === 'editAnnotation') {
      const idx = editingAnnIdxRef.current;
      if (idx >= 0 && idx < s.annotations.length) {
        s.undoStack.push(JSON.parse(JSON.stringify(s.annotations)));
        s.redoStack = [];
        s.annotations[idx].label = v.labelName;
        s.annotations[idx].labelUniqueId = v.labelUniqueId;
      }
      editingAnnIdxRef.current = -1;
      const c: Record<string, number> = {}; labelsRef.current.forEach(l => { c[l.labelUniqueId] = s.annotations.filter(a => a.labelUniqueId === l.labelUniqueId).length; }); setLabelCounts({ ...c });
      closePopup(); return;
    }
    if (!s.draftAnnotation) return;
    s.draftAnnotation.label = v.labelName; s.draftAnnotation.labelUniqueId = v.labelUniqueId; s.draftAnnotation.source = 'user';
    s.undoStack.push(JSON.parse(JSON.stringify(s.annotations))); s.redoStack = [];
    s.annotations.push(s.draftAnnotation!); s.selectedAnnotation = s.annotations.length - 1; s.draftAnnotation = null;
    // New user annotation added — match old editor default actual behavior.
    updateActualFromAnnotations(s.annotations);
    setHasUserAnnotations(true);
    closePopup();
    const c: Record<string, number> = {}; labelsRef.current.forEach(l => { c[l.labelUniqueId] = s.annotations.filter(a => a.labelUniqueId === l.labelUniqueId).length; }); setLabelCounts({ ...c });
  };

  const addLabel = () => { popupModeRef.current = 'addLabel'; setLabelSearch(''); setSelLabel(null); setPopupOpen(true); };

  const isLast = currentPage >= annotLeft;

  const pct = statusUpdated === '2'
    ? (annotDone > 0 ? 100 : 0)
    : (annotDone + annotLeft > 0 ? Math.round((annotDone / (annotDone + annotLeft)) * 100) : 0);
  const filtered = labels.filter(l => l.labelName.toLowerCase().includes(labelSearch.toLowerCase()));
  const searchTrimmed = labelSearch.trim();
  const canAddNew = searchTrimmed && !labels.find(l => l.labelName.toLowerCase() === searchTrimmed.toLowerCase());

  return (
    <div className="vt-overlay">
      <div className="vt-popup-surface">
      <div className="vt-root">

        {/* LEFT SIDEBAR */}
        <div className="vt-sidebar">
          <div className="vt-sidebar-header">
            <div className="vt-stats-row">
              <div className="vt-stat"><div className="num">{annotDone}</div><div className="lbl">Annotation Completed</div></div>
              <div className="vt-stat"><div className="num">{statusUpdated === '1' ? annotLeft : 0}</div><div className="lbl">To Be Annotated</div></div>
            </div>
          </div>
          <div className="vt-scroll">
            {/* Labels accordion */}
            <div className={`vt-accordion${accordionOpen ? ' expanded' : ''}`}>
              <div className="vt-acc-header" onClick={() => setAccordionOpen(o => !o)}>
                <span className="vt-acc-title">Labels</span>
                <div className="vt-acc-right">
                  {ENABLE_LABEL_CREATION && (
                    <button className="vt-sec-btn" disabled={statusUpdated === '2'} onClick={e => { e.stopPropagation(); addLabel(); }}>
                      <span className="material-icons-round" style={{ fontSize: 18 }}>add</span>
                    </button>
                  )}
                  <span className="material-icons-round vt-acc-icon">expand_more</span>
                </div>
              </div>
              <div className="vt-acc-content" style={{ maxHeight: accordionOpen ? 400 : 0 }}>
                <div className="vt-acc-body">
                  <div className="vt-pills-wrap">
                    {labels.map(l => (
                      <div key={l.labelUniqueId} className="vt-pill">
                        {l.labelName}<span className="cnt">{labelCounts[l.labelUniqueId] ?? 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Image Info + Validation — hidden when no images */}
            {!noPending && <>
              <div className="vt-card">
                <div className="vt-card-head">
                  <span className="vt-card-title">Image Description</span>
                  <button className="vt-sec-btn" onClick={() => setShowDeleteImageModal(true)}>
                    <span className="material-icons-round" style={{ fontSize: 18 }}>delete</span>
                  </button>
                </div>
                <div className="vt-info-grid">
                  <div className="vt-info-bubble vt-info-full"><div className="vt-info-lbl">{imgInfo.plateTypeName}</div><div className="vt-info-val">{imgInfo.plateTypeValue}</div></div>
                  <div className="vt-info-bubble"><div className="vt-info-lbl">Date</div><div className="vt-info-val">{imgInfo.date || '—'}</div></div>
                  <div className="vt-info-bubble"><div className="vt-info-lbl">Time</div><div className="vt-info-val">{imgInfo.time || '—'}</div></div>
                  {imgInfo.hasCameraPosition && (
                    <div className="vt-info-bubble vt-info-full vt-info-res"><div className="vt-info-lbl">Camera Position</div><div className="vt-info-val">{imgInfo.cameraPosition}</div></div>
                  )}
                </div>
              </div>

              <div className="vt-val-card">
                <div className="vt-card-head"><span className="vt-card-title">Model Result & Validation</span></div>
                <div className="vt-val-stack">
                  <div className="vt-val-row">
                    <span className="vt-val-lbl">Predicted</span>
                    <span className={imgInfo.predicted === 'notok' ? 'vt-badge-danger' : 'vt-badge-success'}>
                      {imgInfo.predicted === 'notok' ? 'NOT OK' : 'OK'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span className="vt-val-lbl">Actual</span>
                    <div className="vt-toggle-row">
                      <button className={`vt-toggle${actual === 'notok' ? ' d' : ''}`} disabled={statusUpdated === '2'} onClick={() => setCurrentImageActual('2')}>NOT OK</button>
                      <button className={`vt-toggle${actual === 'ok' ? ' s' : ''}`} disabled={statusUpdated === '2' || hasUserAnnotations} onClick={() => setCurrentImageActual('1')}>OK</button>
                    </div>
                  </div>
                </div>
                {imgInfo.hasRange && (
                  <div className="vt-range-row">
                    <span className="vt-range-lbl">Defective plate range</span>
                    <span className="vt-range-badge">{imgInfo.startArea} - {imgInfo.endArea}</span>
                  </div>
                )}
              </div>
            </>}
          </div>
        </div>

        {/* MAIN AREA */}
        <div className="vt-main">
          <div className="vt-topbar">
            <span className="vt-topbar-title">Image Editor</span>
            <div className="vt-top-right">
              <button className="vt-hbtn" onClick={() => isFullscreen ? document.exitFullscreen() : document.documentElement.requestFullscreen()}><span className="material-icons-round">{isFullscreen ? 'fullscreen_exit' : 'fullscreen'}</span></button>
              <button className="vt-hbtn cls" onClick={handleEditorClose}><span className="material-icons-round">close</span></button>
            </div>
          </div>
          <div className="vt-canvas-row">
            <div className="vt-vtoolbar">
              {([['rect','crop_square','Rectangle',ENABLE_RECT_ANNOTATION],['circle','circle','Circle',ENABLE_CIRCLE_ANNOTATION],['freedraw','gesture','Free Draw',ENABLE_FREEDRAW_ANNOTATION]] as const).map(([t,icon,tip,enabled]) => (
                enabled && <button key={t} className={`vt-tbtn${toolUI===t?' active':''}`} disabled={statusUpdated === '2'} onClick={() => { st.current.tool=t; setToolUI(t); }}>
                  <span className="material-icons-round">{icon}</span><span className="tip">{tip}</span>
                </button>
              ))}
              <div className="vt-sep" />
              {([['pan','pan_tool','Pan'],['select','near_me','Select']] as const).map(([t,icon,tip]) => (
                <button key={t} className={`vt-tbtn${toolUI===t?' active':''}`} onClick={() => { st.current.tool=t; setToolUI(t); }}>
                  <span className="material-icons-round">{icon}</span><span className="tip">{tip}</span>
                </button>
              ))}
              <div className="vt-sep" />
              <button className="vt-tbtn" disabled={statusUpdated === '2'} onClick={() => fns.current.undo()}><span className="material-icons-round">undo</span><span className="tip">Undo</span></button>
              <button className="vt-tbtn" disabled={statusUpdated === '2'} onClick={() => fns.current.redo()}><span className="material-icons-round">redo</span><span className="tip">Redo</span></button>
              <div className="vt-sep" />
            </div>
            <div className="vt-canvas-wrap">
              {noPending && !isLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.92)', zIndex: 10, gap: 12 }}>
                  <span className="material-icons-round" style={{ fontSize: 56, color: '#6b7280' }}>{statusUpdated === '2' ? 'image_search' : 'check_circle'}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>{statusUpdated === '2' ? 'No Completed Images Found' : 'No Pending Annotations'}</span>
                  <span style={{ fontSize: 14, color: '#9ca3af' }}>{statusUpdated === '2' ? 'No images match the current filter.' : 'All images have been annotated.'}</span>
                </div>
              )}
              {isLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 10 }}>
                  <span className="material-icons-round" style={{ fontSize: 40, animation: 'spin 1s linear infinite' }}>hourglass_empty</span>
                </div>
              )}
              <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
                <canvas ref={canvasRef} className="vt-canvas" />
              </div>
              {!noPending && (
                <div className="vt-minimap">
                  <canvas ref={mmRef} />
                  <div ref={mmVpRef} className="vt-mm-vp" />
                </div>
              )}
              {!noPending && (
                <div className="vt-zoom-float">
                  <button className="vt-zbtn" onClick={() => fns.current.zoomOut()}><span className="material-icons-round">remove</span></button>
                  <span className="vt-zoom-val">{zoomTxt}</span>
                  <button className="vt-zbtn" onClick={() => fns.current.zoomIn()}><span className="material-icons-round">add</span></button>
                  <div className="vt-sep" />
                  <button className="vt-zbtn" onClick={() => fns.current.zoomFit()}><span className="material-icons-round">fit_screen</span></button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOTTOM NAV */}
        <div className="vt-bottom">
          <div className="vt-progress-area">
            <button className="vt-filter-pill" onClick={() => setIsFiltreModalOpen(true)}>
              <span className="material-icons-round">filter_list</span> Filter
            </button>
            <div className="vt-prog-wrap">
              <div className="vt-prog-track"><div className="vt-prog-fill" style={{ width: pct + '%' }} /></div>
              <span className="vt-prog-text"><strong>{pct}%</strong> complete</span>
            </div>
            <span className="vt-page-info">
              {statusUpdated === '2'
                ? `${annotDone} annotated`
                : `${annotDone} / ${annotDone + annotLeft}`}
            </span>
          </div>
          <div className="vt-nav-btns">
            {statusUpdated === '2' && (
              <button className="vt-nbtn vt-nbtn-out" disabled={isLoading || currentPage <= 1} onClick={async () => {
                const prev = Math.max(1, currentImageCountRef.current - 1);
                await fetchImage(prev);
              }}><span className="material-icons-round">arrow_back</span> Previous</button>
            )}
            <button
              className="vt-nbtn vt-nbtn-pri"
              disabled={isLoading || noPending || (statusUpdated === '2' && isLast)}
              onClick={handleNext}
            >
              {statusUpdated === '2' ? 'Next' : isLast ? 'Save' : 'Save & Next'} <span className="material-icons-round">arrow_forward</span>
            </button>
          </div>
        </div>

      {/* CONTEXT MENU */}
      <div ref={ctxMenuRef} className="vt-ctx">
        <div className="vt-ctx-item" onClick={() => fns.current.deleteSelected()}>
          <span className="material-icons-round">delete</span> Delete
        </div>
      </div>

      {/* DELETE IMAGE MODAL */}
      {showDeleteImageModal && (
        <div className="vt-modal-bg" onClick={e => { if (e.target === e.currentTarget) setShowDeleteImageModal(false); }}>
          <div className="vt-modal" style={{ width: 420, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="vt-modal-x" onClick={() => setShowDeleteImageModal(false)}>
                <span className="material-icons-round">close</span>
              </button>
            </div>
            <span className="material-icons-round" style={{ fontSize: 52, color: 'var(--vt-danger)', marginBottom: 12 }}>delete_forever</span>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--vt-danger)', marginBottom: 8 }}>Delete Image?</h2>
            <p style={{ fontSize: 14, color: 'var(--vt-text2)', marginBottom: 28 }}>
              This image will be permanently removed from annotations and cannot be undone.
            </p>
            <div className="vt-modal-foot" style={{ justifyContent: 'center' }}>
              <button className="vt-mbtn vt-mbtn-ghost" onClick={() => setShowDeleteImageModal(false)}>Cancel</button>
              <button className="vt-mbtn" style={{ background: 'var(--vt-danger)', color: '#fff' }} onClick={handleDeleteImage}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* FILTER MODAL */}
      {isFiltreModalOpen && (
        <ObjectDetectionFilterModal
          handleFiltreModal={() => setIsFiltreModalOpen(false)}
          startValue={startValue}
          setStartValue={setStartValue}
          endValue={endValue}
          setEndValue={setEndValue}
          statusValue={statusValue}
          annotationStatus={annotationStatus}
          handleInputChange={handleInputChange}
          predictionFiltreOption={predictionFiltreOption}
          predictionValue={predictionValue}
          actualValue={actualValue}
          modelDefectLabels={modelDefectLabels}
          SelectedModelDefect={SelectedModelDefect}
          handleFormSubmit={handleFormSubmit}
        />
      )}

      {/* LABEL POPUP */}
      {popupOpen && (
        <div className="vt-modal-bg">
          <div className="vt-modal vt-lbl-modal">
            <div className="vt-modal-top"><h2>{popupModeRef.current === 'addLabel' ? 'Add Label' : 'Select Label'}</h2><button className="vt-modal-x" onClick={() => closePopup(true)}><span className="material-icons-round">close</span></button></div>
            <input
              autoFocus
              className="vt-lbl-search"
              placeholder="Search labels..."
              value={labelSearch}
              onChange={e => { setLabelSearch(e.target.value); setSelLabel(null); }}
              onKeyDown={e => e.key === 'Enter' && (popupModeRef.current === 'addLabel' || ENABLE_LABEL_CREATION || selLabel !== null) && confirmLabel()}
            />
            <div className="vt-lbl-list">
              {filtered.map(l => (
                <div key={l.labelUniqueId} className={`vt-lbl-opt${selLabel?.labelUniqueId===l.labelUniqueId?' sel':''}`} onClick={() => setSelLabel(l)}>
                  <span>{l.labelName}</span>
                  {selLabel?.labelUniqueId === l.labelUniqueId && <span className="material-icons-round vt-lbl-check">check</span>}
                </div>
              ))}
              {canAddNew && ENABLE_LABEL_CREATION && (
                <div className="vt-lbl-opt vt-lbl-add" onClick={() => setSelLabel({ labelName: searchTrimmed, labelUniqueId: `label-${generateUniqueRandomKey()}` })}>
                  <span className="material-icons-round" style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }}>add</span>
                  Add "{searchTrimmed}"
                </div>
              )}
            </div>
            <div className="vt-modal-foot">
              <button className="vt-mbtn vt-mbtn-ghost" onClick={() => closePopup(true)}>Cancel</button>
              <button className="vt-mbtn vt-mbtn-pri" onClick={confirmLabel} disabled={popupModeRef.current !== 'addLabel' && !ENABLE_LABEL_CREATION && selLabel === null}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );

}

export default VisionTraining;
